import React, { useEffect } from "react";
import { useMemo, useRef, useState } from "react";
import Renderer from "../renderer/Renderer";
import HotspotPopUp from "../editor/HotspotPopUp";
import apiclient from "../../apiclient";
import { PROJECT_ACCESS, PROJECT_SHARE_LIST, SCENE_LIST_HIERARCHY } from "../../apiclient/apiEndpoints";
import { UnoStoreHooks } from "@simulanisindia/ui";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { redirectToUno } from "../../utils/common";
import { QuestionIcon, QuestionMainIcon, VolumeIcon, VolumeMuteIcon } from "../../assets/icons/icons";
import { HelperBox, OverLay } from "../editor/ChatBot";
import { generateLowResUrl } from "../../utils/threeUtils";
import IntroModal from "../../components/modals/IntroModal";
// Enhanced logging utility with error context
const createLogger = (component = "View") => {
  const log = (level, message, data = null) => {
    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] [${component}] [${level.toUpperCase()}] ${message}`;
    if (data) {
      console[level](logMessage, data);
    } else {
      console[level](logMessage);
    }
  };

  const logError = (message, error, context = {}) => {
    const errorData = {
      message,
      error: {
        name: error?.name,
        message: error?.message,
        stack: error?.stack,
        type: typeof error,
      },
      context: {
        ...context,
        timestamp: new Date().toISOString(),
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
        url: typeof window !== "undefined" ? window.location.href : "unknown",
      },
    };
    log("error", message, errorData);
  };

  return {
    info: (message, data) => log("info", message, data),
    warn: (message, data) => log("warn", message, data),
    error: (message, data) => log("error", message, data),
    errorWithContext: (message, error, context) => logError(message, error, context),
    debug: (message, data) => log("debug", message, data),
    log: (message, data) => log("log", message, data),
  };
};

const logger = createLogger("View");
// Performance monitoring utility
const performanceMonitor = {
  start: (operation) => {
    const startTime = performance.now();
    logger.debug(`Performance: Starting ${operation}`, { startTime });
    return startTime;
  },
  end: (operation, startTime, context = {}) => {
    const endTime = performance.now();
    const duration = endTime - startTime;
    logger.info(`Performance: Completed ${operation}`, {
      duration: `${duration.toFixed(2)}ms`,
      ...context,
    });
    return duration;
  },
};
// Singleton in-memory preload cache
const imagePreloadCache = new Map();
const { useUnoSelector } = UnoStoreHooks;
const View = () => {
  const { uuid } = useParams();
  const location = useLocation();
  logger.info("View component initializing");
  const configData = useUnoSelector((state) => state.config);
  const API_URL = configData?.config?.API_URL;
  const VIEWER_WALKTHROUGH_LINK = configData?.config?.VIEWER_WALKTHROUGH_LINK;
  const userData = useUnoSelector((state) => state.user);
  const [isLoading, setIsLoading] = useState(true);
  const [showIntroModal, setShowIntroModal] = useState(location?.state?.openIntroModal ?? false);
  const [introModalDetails, setIntroModalDetails] = useState({
    title: location?.state?.title ?? ""
  });
  const [sceneList, setSceneList] = useState([]);
  const [currentSceneUuid, setCurrentSceneUuid] = useState(null);
  const [visibleHotspotIds, setVisibleHotspotIds] = useState(() => new Set());
  const hotspotTimersRef = useRef([]);
  const [transitionPhase, setTransitionPhase] = useState("idle"); // idle | out | in
  const [transformOrigin, setTransformOrigin] = useState("50% 50%");
  const [showHotspotModal, setShowHotspotModal] = useState(false);
  const [currentHotspot, setCurrentHotspot] = useState(null);
  const [openHelperBox, setOpenHelperBox] = useState(false);
  const [isLow1, setIsLow1] = useState(() => {
    const low1State = typeof window !== "undefined" && window._viewerIsLow1 ? true : false;
    logger.debug("Initial isLow1 state", { low1State, windowViewerIsLow1: window._viewerIsLow1 });
    return low1State;
  });
  const [isLow, setIsLow] = useState(() => {
    const lowState = typeof window !== "undefined" && window._viewerIsLow ? true : false;
    logger.debug("Initial isLow state", { lowState, windowViewerIsLow: window._viewerIsLow });
    return lowState;
  });
  const [isHighResReady, setIsHighResReady] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [sampledata, setSampleData] = useState([]);
  const [muted, setMuted] = useState(true)
  const navigate = useNavigate();

  const handleCloseIntroModal = () => {
    setShowIntroModal(false)
  }

  // Ensure transition styles are present once
  useEffect(() => {
    logger.debug("Setting up transition styles");
    if (typeof document !== "undefined" && !document.getElementById("viewer-transition-styles")) {
      logger.info("Creating viewer transition styles");
      const style = document.createElement("style");
      style.id = "viewer-transition-styles";
      style.textContent = `
        .viewer-root { position: relative; width: 100%; height: 100vh; overflow: hidden; background: #000; }
        .viewer-canvas-wrap { width: 100%; height: 100%; will-change: transform, filter; transform-origin: center center; }
        .zoom-blur-out { animation: viewerZoomBlurOut var(--zoom-out-duration, 1200ms) ease-out forwards; }
        .zoom-fade-in { animation: viewerZoomIn 320ms ease forwards, viewerFadeIn 500ms ease forwards; }
        @keyframes viewerZoomBlurOut { 
          from { transform: scale(1); filter: blur(0px); } 
          to { transform: scale(1.8); filter: blur(20px); } 
        }
        @keyframes viewerZoomIn { from { transform: scale(0.96); } to { transform: scale(1); } }
        @keyframes viewerFadeIn { from { opacity: 0.25; } to { opacity: 1; } }
        .loader-overlay { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(0,0,0,0.2); z-index: 5; }
        .viewer-fullsize { position: absolute; inset: 0; }
      `;
      document.head.appendChild(style);
      logger.info("Viewer transition styles created and appended");
    } else {
      logger.debug("Viewer transition styles already exist");
    }
  }, []);

  // Listen for low1 progressive texture events to toggle heavy blur
  useEffect(() => {
    logger.debug("Setting up viewer-low1 event listener");
    const handler = (e) => {
      const active = !!(e && e.detail && e.detail.active);
      logger.info("viewer-low1 event received", { active, eventDetail: e.detail });
      setIsLow1(active);
    };
    window.addEventListener("viewer-low1", handler);
    logger.debug("viewer-low1 event listener registered");
    return () => {
      logger.debug("Cleaning up viewer-low1 event listener");
      window.removeEventListener("viewer-low1", handler);
    };
  }, []);

  // Listen for high-res texture ready events
  useEffect(() => {
    logger.debug("Setting up high-res ready event listener");
    const handler = (e) => {
      const ready = !!(e && e.detail && e.detail.ready);
      logger.info("High-res texture ready event received", { ready, eventDetail: e.detail });
      setIsHighResReady(ready);

      // If we're not transitioning and high-res is ready, we can stop loading
      if (ready && !isTransitioning) {
        logger.info("High-res ready and not transitioning, setting loading to false");
        setIsLoading(false);
      }
    };
    window.addEventListener("viewer-highres-ready", handler);
    logger.debug("High-res ready event listener registered");
    return () => {
      logger.debug("Cleaning up high-res ready event listener");
      window.removeEventListener("viewer-highres-ready", handler);
    };
  }, [isTransitioning]);

  useEffect(() => {
    logger.debug("Setting up viewer-low event listener");
    const handler = (e) => {
      const active = !!(e && e.detail && e.detail.active);
      logger.info("viewer-low event received", { active, eventDetail: e.detail });
      setIsLow(active);
    };
    window.addEventListener("viewer-low", handler);
    logger.debug("viewer-low event listener registered");
    return () => {
      logger.debug("Cleaning up viewer-low event listener");
      window.removeEventListener("viewer-low", handler);
    };
  }, []);

  // const findSceneByUuid = (useCallbackUuid) => (uuid) => {
  //   return sceneList.find((s) => s.uuid === uuid) || null;
  // };

  // Enhanced image preloader with low-res fallback
  const preloadImage = (url) => {
    if (!url) {
      return Promise.reject(new Error("Empty URL"));
    }

    // Skip low-res fallback for videos (like .mp4)
    const isVideo = /\.(mp4|webm|ogg)$/i.test(url);
    if (isVideo) {
      // Just resolve immediately, no preload
      return Promise.resolve(null);
    }

    if (imagePreloadCache.has(url)) {
      return imagePreloadCache.get(url);
    }

    const startTime = performance.now();

    const promise = new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        const loadTime = performance.now() - startTime;
        resolve(img);
      };

      img.onerror = (e) => {
        const loadTime = performance.now() - startTime;

        // Skip fallback for videos
        if (isVideo) {
          reject(e);
          return;
        }

        // Try low-res fallback for images
        const lowResUrl = generateLowResUrl(url);
        if (lowResUrl !== url) {
          const lowResImg = new Image();
          lowResImg.crossOrigin = "anonymous";
          lowResImg.onload = () => {
            resolve(lowResImg);
          };
          lowResImg.onerror = () => {
            reject(new Error("Both high-res and low-res images failed to load"));
          };
          lowResImg.src = lowResUrl;
        } else {
          reject(e);
        }
      };

      img.src = url;
    }).catch(() => null);

    imagePreloadCache.set(url, promise);
    return promise;
  };



  const scheduleHotspotTimers = (scene) => {
    logger.info("scheduleHotspotTimers called", {
      sceneUuid: scene?.uuid,
      sceneName: scene?.name,
      existingTimers: hotspotTimersRef.current.length,
    });

    // Clear existing timers
    const clearedCount = hotspotTimersRef.current.length;
    hotspotTimersRef.current.forEach((t) => clearTimeout(t));
    hotspotTimersRef.current = [];
    setVisibleHotspotIds(new Set());
    logger.debug("Cleared existing hotspot timers", { clearedCount });

    const hotspots = Array.isArray(scene?.hotspots) ? scene.hotspots : [];
    logger.debug("Processing hotspots for timers", {
      hotspotCount: hotspots.length,
      hotspots: hotspots.map((h) => ({
        uuid: h.uuid,
        type: h.type,
        timerStart: h?.timer?.start,
      })),
    });

    hotspots.forEach((h, index) => {
      const startSec = typeof h?.timer?.start === "number" ? h.timer.start : 2;
      const delayMs = Math.max(0, startSec * 1000);

      logger.debug("Scheduling hotspot timer", {
        hotspotUuid: h.uuid,
        hotspotType: h.type,
        startSec,
        delayMs,
        index,
      });

      const timerId = setTimeout(() => {
        logger.info("Hotspot timer triggered - making visible", {
          hotspotUuid: h.uuid,
          hotspotType: h.type,
          scheduledDelay: delayMs,
        });
        setVisibleHotspotIds((prev) => {
          const newSet = new Set(prev).add(h.uuid);
          logger.debug("Updated visible hotspot IDs", {
            hotspotUuid: h.uuid,
            previousCount: prev.size,
            newCount: newSet.size,
            allVisible: Array.from(newSet),
          });
          return newSet;
        });
      }, delayMs);

      hotspotTimersRef.current.push(timerId);
    });

    logger.info("scheduleHotspotTimers completed", {
      scheduledTimers: hotspotTimersRef.current.length,
      totalHotspots: hotspots.length,
    });
  };

  const fetchSceneList = async () => {
    const perfStart = performanceMonitor.start("fetchSceneList");
    logger.info("fetchSceneList started");
    try {
      logger.debug("Setting scene list from sampledata", { sceneCount: sampledata.length });
      let data = await apiclient.get(`${API_URL}${SCENE_LIST_HIERARCHY}/${uuid}`);
      setSampleData(data);
      setSceneList(data);

      // Detect default scene
      const defaultScene = data.find((s) => s.default) || data[0];
      logger.info("Default scene detected", {
        defaultSceneUuid: defaultScene?.uuid,
        defaultSceneName: defaultScene?.name,
        isDefault: defaultScene?.default,
        fallbackToFirst: !data.find((s) => s.default),
      });

      if (defaultScene) {
        logger.info("Setting current scene and starting preload process", {
          sceneUuid: defaultScene.uuid,
          sceneName: defaultScene.name,
          inventoryPath: defaultScene.inventory?.path,
        });

        setCurrentSceneUuid(defaultScene.uuid);
        setIsLoading(true);

        // Reset zoom to default when loading new scene
        if (window._resetCanvasZoom) {
          window._resetCanvasZoom();
        }
        if (window._resetScene360Zoom) {
          window._resetScene360Zoom();
        }

        // Preload main scene image
        logger.info("Preloading default scene image", { path: defaultScene.inventory?.path });
        await preloadImage(defaultScene.inventory?.path);

        // Schedule hotspot timers
        logger.info("Scheduling hotspot timers for default scene");
        scheduleHotspotTimers(defaultScene);

        // Preload LINK destinations
        const linkTargets = (defaultScene.hotspots || [])
          .filter((h) => h.type === "LINK" && h.destination)
          .map((h) => data.find((s) => s.uuid === h.destination))
          .filter(Boolean);

        logger.info("Preloading link target scenes", {
          linkTargetCount: linkTargets.length,
          linkTargets: linkTargets.map((sc) => ({
            uuid: sc.uuid,
            name: sc.name,
            path: sc.inventory?.path,
          })),
        });

        linkTargets.forEach((sc) => {
          logger.debug("Preloading link target", {
            targetUuid: sc.uuid,
            targetPath: sc.inventory?.path,
          });
          preloadImage(sc.inventory?.path);
        });

        logger.info("Default scene setup completed, waiting for high-res texture");
        // Don't set loading to false here - wait for high-res texture ready event
        performanceMonitor.end("fetchSceneList", perfStart, {
          sceneCount: data.length,
          hasDefaultScene: true,
          defaultSceneUuid: defaultScene.uuid,
        });
      } else {
        logger.warn("No default scene found, setting loading to false");
        setIsLoading(false);
        performanceMonitor.end("fetchSceneList", perfStart, {
          sceneCount: data.length,
          hasDefaultScene: false,
        });
      }
    } catch (error) {
      logger.errorWithContext("fetchSceneList failed", error, {
        operation: "fetchSceneList",
        sceneCount: sampledata?.length,
        hasDefaultScene: !!sampledata?.find((s) => s.default),
        currentSceneUuid,
        isLoading,
      });
      setIsLoading(false);
      performanceMonitor.end("fetchSceneList", perfStart, {
        success: false,
        error: error.message,
      });
    }
  };

  const getProjectShareList = async (_projectId) => {
    try {
      const { usersList } = await apiclient.get(`${API_URL}${PROJECT_SHARE_LIST}/${_projectId}`);
      const currentUserUUID = userData?.user?.uuid;
      const currentGroupUUID = userData?.user?.group?.uuid;
      const hasAccess = usersList.some((user) => user.uuid === currentUserUUID || user.uuid === currentGroupUUID);
      if (hasAccess === false) navigate("/");
    } catch (error) {
      console.error("Error fetching project", error);
    }
  };

  const fetchProjectAccess = async () => {
    try {
      const response = await apiclient.get(`${API_URL}${PROJECT_ACCESS}/${uuid}`);
      const isPublic = response?.access?.public;
      if (isPublic === false) {
        if (userData?.token) {
          await getProjectShareList(uuid);
        } else {
          redirectToUno({ configData });
        }
      }
    } catch (error) {
      console.error("Error fetching", error);
    }
  };

  const init = async () => {
    await fetchProjectAccess();
    fetchSceneList();
  };

  useEffect(() => {
    init();
    logger.info("Component mounted, initializing scene list");
    return () => {
      logger.info("Component unmounting, cleaning up hotspot timers", {
        timerCount: hotspotTimersRef.current.length,
      });
      hotspotTimersRef.current.forEach((t) => clearTimeout(t));
      hotspotTimersRef.current = [];
    };
  }, [uuid]);

  // State change monitoring effects
  useEffect(() => {
    logger.info("isLoading state changed", { isLoading });
  }, [isLoading]);

  useEffect(() => {
    logger.info("sceneList state changed", {
      sceneCount: sceneList.length,
      sceneUuids: sceneList.map((s) => s.uuid),
    });
  }, [sceneList]);

  useEffect(() => {
    logger.info("currentSceneUuid state changed", {
      currentSceneUuid,
      previousScene: sceneList.find((s) => s.uuid !== currentSceneUuid)?.uuid,
    });
  }, [currentSceneUuid, sceneList]);

  useEffect(() => {
    logger.info("visibleHotspotIds state changed", {
      visibleCount: visibleHotspotIds.size,
      visibleIds: Array.from(visibleHotspotIds),
    });
  }, [visibleHotspotIds]);

  useEffect(() => {
    logger.info("transitionPhase state changed", {
      transitionPhase,
      transformOrigin,
    });
  }, [transitionPhase, transformOrigin]);

  useEffect(() => {
    logger.info("isLow1 state changed", { isLow1 });
  }, [isLow1]);

  useEffect(() => {
    logger.info("isLow state changed", { isLow });
  }, [isLow]);

  useEffect(() => {
    logger.info("isHighResReady state changed", { isHighResReady });
  }, [isHighResReady]);

  useEffect(() => {
    logger.info("isTransitioning state changed", { isTransitioning });
  }, [isTransitioning]);

  useEffect(() => {
    logger.info("showHotspotModal state changed", {
      showHotspotModal,
      hasCurrentHotspot: !!currentHotspot,
      currentHotspotUuid: currentHotspot?.uuid,
      currentHotspotAction: currentHotspot?.action?.[0],
      currentHotspotTitle: currentHotspot?.action?.[0]?.title,
      currentHotspotDescription: currentHotspot?.action?.[0]?.description,
    });
  }, [showHotspotModal, currentHotspot]);

  const currentScene = useMemo(() => {
    const scene = sceneList.find((s) => s.uuid === currentSceneUuid) || null;
    logger.debug("currentScene computed", {
      currentSceneUuid,
      foundScene: !!scene,
      sceneName: scene?.name,
      totalScenes: sceneList.length,
    });
    return scene;
  }, [sceneList, currentSceneUuid]);

  const visibleHotspots = useMemo(() => {
    if (!currentScene) {
      logger.debug("visibleHotspots computed: No current scene", { currentScene: null });
      return [];
    }

    const all = Array.isArray(currentScene.hotspots) ? currentScene.hotspots : [];
    const visible = all.filter((h) => visibleHotspotIds.has(h.uuid)).map((h) => ({ ...h, timer: { start: 0 } }));

    logger.debug("visibleHotspots computed", {
      currentSceneUuid: currentScene.uuid,
      totalHotspots: all.length,
      visibleCount: visible.length,
      visibleHotspotIds: Array.from(visibleHotspotIds),
      visibleHotspots: visible.map((h) => ({ uuid: h.uuid, type: h.type })),
    });

    return visible;
  }, [currentScene, visibleHotspotIds]);

  const startSceneTransition = async (targetUuid, clickPoint) => {
    const perfStart = performanceMonitor.start("startSceneTransition");
    logger.info("startSceneTransition called", {
      targetUuid,
      clickPoint,
      currentSceneUuid,
      transitionPhase,
    });

    try {
      const targetScene = sceneList.find((s) => s.uuid === targetUuid);
      if (!targetScene) {
        logger.error("startSceneTransition failed: Target scene not found", {
          targetUuid,
          availableScenes: sceneList.map((s) => ({ uuid: s.uuid, name: s.name })),
        });
        return;
      }

      logger.info("Starting scene transition", {
        fromScene: currentSceneUuid,
        toScene: targetUuid,
        targetSceneName: targetScene.name,
        targetScenePath: targetScene.inventory?.path,
      });

      // Set transitioning state to prevent loading state changes
      setIsTransitioning(true);
      setIsHighResReady(false);

      // Transition origin from click point
      let transformOriginValue = "50% 50%";
      if (clickPoint && clickPoint.clientRect) {
        transformOriginValue = `${clickPoint.clientRect.x}% ${clickPoint.clientRect.y}%`;
        logger.debug("Using clientRect for transform origin", {
          x: clickPoint.clientRect.x,
          y: clickPoint.clientRect.y,
        });
      } else if (clickPoint && typeof clickPoint.x === "number" && typeof clickPoint.y === "number") {
        transformOriginValue = `${clickPoint.x}px ${clickPoint.y}px`;
        logger.debug("Using pixel coordinates for transform origin", {
          x: clickPoint.x,
          y: clickPoint.y,
        });
      } else {
        logger.debug("Using default center transform origin");
      }

      setTransformOrigin(transformOriginValue);
      logger.debug("Transform origin set", { transformOrigin: transformOriginValue });

      // Start zoom out animation immediately
      logger.info('Setting transition phase to "out"');
      setTransitionPhase("out");

      // Apply zoom out duration
      const zoomOutDuration = 1200;
      document.documentElement.style.setProperty("--zoom-out-duration", `${zoomOutDuration}ms`);

      // Preload target image with proper sequence
      const targetImageUrl = targetScene.inventory?.path;
      const isPreloaded = imagePreloadCache.has(targetImageUrl);

      let targetImageReady = false;

      if (isPreloaded) {
        logger.info("Target scene image already preloaded", {
          targetPath: targetImageUrl,
        });
        targetImageReady = true;
      } else {
        // Try to preload the target image
        logger.info("Preloading target scene image", {
          targetPath: targetImageUrl,
        });

        try {
          const preloadStartTime = performance.now();
          await preloadImage(targetImageUrl);
          const preloadTime = performance.now() - preloadStartTime;
          logger.info("Target scene image preloaded successfully", {
            targetPath: targetImageUrl,
            preloadTime: `${preloadTime.toFixed(2)}ms`,
          });
          targetImageReady = true;
        } catch (error) {
          logger.warn("Failed to preload target image, will wait for high-res ready event", {
            targetPath: targetImageUrl,
            error: error.message,
          });
          targetImageReady = false;
        }
      }

      // Function to switch scene when image is ready
      const switchSceneWhenReady = () => {
        logger.info("Executing scene switch", {
          newSceneUuid: targetScene.uuid,
          newSceneName: targetScene.name,
        });

        setCurrentSceneUuid(targetScene.uuid);

        // Reset zoom to default when switching scenes
        if (window._resetCanvasZoom) {
          window._resetCanvasZoom();
        }
        if (window._resetScene360Zoom) {
          window._resetScene360Zoom();
        }

        // After switching scene, schedule hotspot timers
        logger.info("Scheduling hotspot timers for new scene");
        scheduleHotspotTimers(targetScene);

        // Preload link targets of the new scene
        const linkTargets = (targetScene.hotspots || [])
          .filter((h) => h.type === "LINK" && h.destination)
          .map((h) => sceneList.find((s) => s.uuid === h.destination))
          .filter(Boolean);

        logger.info("Preloading link targets for new scene", {
          linkTargetCount: linkTargets.length,
          linkTargets: linkTargets.map((sc) => ({
            uuid: sc.uuid,
            name: sc.name,
            path: sc.inventory?.path,
          })),
        });

        linkTargets.forEach((sc) => {
          logger.debug("Preloading new scene link target", {
            targetUuid: sc.uuid,
            targetPath: sc.inventory?.path,
          });
          preloadImage(sc.inventory?.path);
        });

        // Wait for high-res texture ready signal, then fade in
        logger.info("Setting up high-res ready event handler for transition completion");
        let done = false;
        const beginIn = () => {
          if (done) {
            logger.debug("beginIn called but already done, skipping");
            return;
          }
          done = true;
          logger.info("Beginning fade-in transition", {
            sceneUuid: targetScene.uuid,
            sceneName: targetScene.name,
          });
          setTransitionPhase("in");
          setIsTransitioning(false);
          setTimeout(() => {
            logger.info("Transition completed, setting phase to idle");
            setTransitionPhase("idle");
          }, 600);
        };

        const handler = (e) => {
          logger.debug("high-res ready event handler triggered", {
            eventDetail: e.detail,
            ready: e?.detail?.ready,
          });
          if (e?.detail?.ready) {
            logger.info("High-res texture ready, removing handler and beginning fade-in");
            window.removeEventListener("viewer-highres-ready", handler);
            beginIn();
          }
        };

        window.addEventListener("viewer-highres-ready", handler);
        logger.debug("High-res ready event handler registered");

        // Fallback in case event isn't received
        setTimeout(() => {
          logger.warn("High-res ready event fallback triggered - event not received within 2000ms");
          window.removeEventListener("viewer-highres-ready", handler);
          beginIn();
        }, 2000);
      };

      // Determine when to switch scene based on preload status
      if (targetImageReady) {
        setTimeout(() => {
          switchSceneWhenReady();
        }, zoomOutDuration);
      } else {
        const imageReadyHandler = (e) => {
          if (e?.detail?.ready) {
            window.removeEventListener("viewer-highres-ready", imageReadyHandler);
            switchSceneWhenReady();
          }
        };

        window.addEventListener("viewer-highres-ready", imageReadyHandler);

        // Fallback timeout - switch scene even if image isn't ready
        setTimeout(() => {
          logger.warn("Image ready timeout - switching scene anyway");
          window.removeEventListener("viewer-highres-ready", imageReadyHandler);
          switchSceneWhenReady();
        }, zoomOutDuration + 1000); // Wait a bit longer than zoom out
      }
    } catch (error) {
      performanceMonitor.end("startSceneTransition", perfStart, {
        success: false,
        error: error.message,
      });
    }
  };

  const handleRendererHotspotClick = (hotspotUuid) => {
    try {
      if (!currentScene) {
        return;
      }

      const hotspot = (currentScene.hotspots || []).find((h) => h.uuid === hotspotUuid);
      if (!hotspot) {
        return;
      }
      if (hotspot.type === "LINK" && hotspot.destination) {
        startSceneTransition(hotspot.destination);
      } else if (hotspot.type === "MEDIA") {
        setCurrentHotspot(hotspot);
        // Small delay to ensure hotspot is set before modal opens
        setTimeout(() => {
          setShowHotspotModal(true);
        }, 10);
      } else {
      }
    } catch (error) {
      console.log(error)
    }
  };

  const canvasWrapClass = `viewer-canvas-wrap ${transitionPhase === "out" ? "zoom-blur-out" : transitionPhase === "in" ? "zoom-fade-in" : ""
    }`;

  logger.debug("Render phase", {
    isLoading,
    hasCurrentScene: !!currentScene,
    currentSceneUuid,
    transitionPhase,
    visibleHotspotCount: visibleHotspotIds.size,
    isLow1,
    isLow,
    isHighResReady,
    isTransitioning,
    showHotspotModal,
    hasCurrentHotspot: !!currentHotspot,
  });


  const handleMuteToggle = () => {
    // Only allow mute toggle if scene.isMuted is false
    if (currentScene.isMuted) {
      console.log("Cannot toggle mute - scene is set to muted");
      return;
    }

    // Only update local muted state - let Renderer handle video muted property
    setMuted(!muted);
  };

  return isLoading && !currentScene ? (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        {logger.debug("Rendering initial loading state")}
        <svg style={{ width: 48, height: 48 }} viewBox="0 0 50 50" aria-label="Loading">
          <circle
            cx="25"
            cy="25"
            r="20"
            fill="none"
            stroke="#9e9e9e"
            strokeWidth="5"
            strokeLinecap="round"
            strokeDasharray="31.4 31.4"
          >
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 25 25"
              to="360 25 25"
              dur="1s"
              repeatCount="indefinite"
            />
          </circle>
        </svg>
      </div>
    </>
  ) : (
    <>
      <div className="viewer-root" data-low1={isLow1 ? "true" : "false"} data-low={isLow ? "true" : "false"}>
        {isLoading && (
          <div className="loader-overlay">
            <svg style={{ width: 48, height: 48 }} viewBox="0 0 50 50" aria-label="Loading">
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="#9e9e9e"
                strokeWidth="5"
                strokeLinecap="round"
                strokeDasharray="31.4 31.4"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 25 25"
                  to="360 25 25"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
          </div>
        )}

        <div
          className={canvasWrapClass}
          style={{
            transformOrigin,
            filter: isTransitioning ? "none" : isLow1 || isLow ? "blur(30px)" : "none",
            transition: "filter 200ms ease",
          }}
        >
          {currentScene && (
            <div className="viewer-fullsize ThreeDViewerC" style={{ position: "relative" }}>
              <Renderer
                sceneDetails={{ ...currentScene, hotspots: visibleHotspots }}
                muted={muted}
                onLoaded={() => {
                  setIsLoading(false);
                }}
                loading={isLoading}
                editorMode={false}
                onHotspotClick={handleRendererHotspotClick}
                isSelectMode={false}
              />
            </div>
          )}
        </div>

        <HotspotPopUp
          currentHotspot={currentHotspot}
          showHotspotModal={showHotspotModal}
          onClose={() => {
            setShowHotspotModal(false);
          }}
          toggleHotspotModal={(show) => {
            setShowHotspotModal(show);
          }}
        />
        <div className="quickTools" onClick={(e) => {
          e.stopPropagation();
          setOpenHelperBox(true)
        }}>
            {currentScene.inventory.type === "VIDEO" &&
              (currentScene.isMuted ? (
                <span className="fullScreen muted-scene">
                  <VolumeMuteIcon />
                </span>
              ) : (
                <span onClick={(e) => {
                  e.stopPropagation();
                  handleMuteToggle()
                }} className="fullScreen">
                  {muted ? <VolumeMuteIcon /> : <VolumeIcon />}
                </span>
              ))}
            <span className="fullScreen help">? Help</span>
          </div>
      </div>
      {openHelperBox && <HelperBox closeHandler={() => setOpenHelperBox(false)} iframeLink={VIEWER_WALKTHROUGH_LINK} />}
      {openHelperBox && <OverLay closeHandler={() => setOpenHelperBox(false)} />}
      <IntroModal show={showIntroModal} handleClose={handleCloseIntroModal} introModalDetails={introModalDetails} />
    </>
  );
};

export default View;
